import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Lightbulb } from 'lucide-react';
import { facebookMetaOptionsService, InterestSearchResult } from '@/services/facebook-meta-options.service';
import { Input } from '@/components/Input';

export interface SelectedInterest { id: string; name: string; }

interface Props {
  selected: SelectedInterest[];
  onChange: (interests: SelectedInterest[]) => void;
}

function useDebounce<T>(v: T, ms = 400) {
  const [dv, setDv] = useState(v);
  useEffect(() => { const t = setTimeout(() => setDv(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return dv;
}

function formatAudience(n?: number) {
  if (!n) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

export const InterestsPicker: React.FC<Props> = ({ selected, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<InterestSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const dq = useDebounce(query);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const data = await facebookMetaOptionsService.searchInterests(q);
      setResults(data.slice(0, 15));
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { search(dq); }, [dq, search]);

  const add = (r: InterestSearchResult) => {
    if (selected.some(s => s.id === r.id)) return;
    onChange([...selected, { id: r.id, name: r.name }]);
    setQuery(''); setResults([]);
  };

  const remove = (id: string) => onChange(selected.filter(s => s.id !== id));

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search interests…"
          iconLeft={<Search size={14} />}
        />
        {(results.length > 0 || loading) && (
          <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-xl border border-border bg-surface-elevated shadow-elevated max-h-56 overflow-y-auto">
            {loading && <div className="px-3 py-2 text-xs text-text-muted">Searching…</div>}
            {results.map(r => (
              <button
                key={r.id}
                type="button"
                onClick={() => add(r)}
                className="flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-surface-alt text-left"
              >
                <Lightbulb size={12} className="text-warning shrink-0" />
                <span className="flex-1 font-medium text-text-primary">{r.name}</span>
                {r.audience_size_lower_bound && (
                  <span className="text-text-muted">
                    ~{formatAudience(r.audience_size_lower_bound)}–{formatAudience(r.audience_size_upper_bound)}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {selected.map(s => (
          <span
            key={s.id}
            className="inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning-soft px-2.5 py-1 text-xs font-medium text-warning"
          >
            <Lightbulb size={10} />
            {s.name}
            <button type="button" onClick={() => remove(s.id)} className="hover:text-danger ml-0.5">
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};
